import {
  Check,
  Circle,
  FileText,
  type LucideIcon,
  Minus,
  Package,
  Palette,
  Tag,
  Target,
  Truck,
  X,
} from "lucide-react";

import type { ToolStep } from "@intelligems/ig-types/schemas/ai/tool-progress";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Stack from "@mui/material/Stack";
import { Shimmer } from "@src/components/ai-elements/shimmer";
import { cn } from "@src/lib/utils";

/**
 * The steps a single tool call reported while it ran.
 *
 * A tool that reads several things at once is one call to the model and
 * several steps to the person waiting. This is the second, drawn inside the
 * card that already represents the call rather than in a second place.
 *
 * State is carried twice on purpose. A running step shimmers *and* is worded
 * in the present tense — "Reviewing your price history" becomes "Reviewed your
 * price history" — so the row still reads correctly in a screenshot, to a
 * reader who cannot see the animation, and under `prefers-reduced-motion`.
 * The tool owns both wordings and sends whichever applies with each report.
 *
 * Three glyphs, and each says a different thing:
 *
 * - `ok` — a check. It happened.
 * - `unavailable` — a dash. It looked and there was nothing there. Nothing
 *   went wrong, so nothing is coloured.
 * - `failed` — a cross in the error colour. It went wrong.
 *
 * The cross is red because a step that always goes green looks identical on
 * exactly the stores where the merchant needs to know it did not. Red is the
 * only saturated colour in this panel, which is the point: it is also the only
 * place something has gone wrong, and the row's own dimming keeps it from
 * shouting.
 *
 * `failed` drew the dash until both states shared it, which was the worst of
 * both: a dash reads as "skipped", so real errors passed for routine, and the
 * routine ones had no glyph of their own to fall back to. Splitting the status
 * gave the dash back the meaning it already looked like it had.
 */
export const ToolSteps = ({
  steps,
  compact,
  className,
}: {
  steps: ToolStep[];
  compact?: boolean;
  /**
   * Merged last, so a surface can change the block's inset. The padding is
   * Tailwind rather than `sx` for exactly that reason: an `sx` value and a
   * caller's utility class are both a single class, so which one won would come
   * down to stylesheet order. Through `cn` the caller simply wins.
   */
  className?: string;
}) => (
  <Stack
    spacing={compact ? 1 : 1.5}
    className={cn(compact ? "px-2.5" : "px-3", "py-2", className)}
  >
    {steps.map((step) => {
      const label = step.label ?? step.id;

      return (
        <Stack
          key={step.id}
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            // A queued row is dimmed further than a finished one: it is the
            // only state that has not happened yet, and it should read as the
            // plan rather than as part of the account.
            opacity:
              step.status === "pending"
                ? 0.45
                : step.status === "failed" || step.status === "unavailable"
                  ? 0.6
                  : 1,
          }}
        >
          <StepIcon status={step.status} icon={step.icon} />

          {step.status === "running" ? (
            <Shimmer as="span" className="text-xs">
              {label}
            </Shimmer>
          ) : (
            // Everything else, `pending` included, is still text. Only the row
            // actually being worked on moves.
            <IgTypography fontSize="12px">{label}</IgTypography>
          )}

          {step.reason ? (
            <IgTypography fontSize="12px" sx={{ color: "text.secondary" }}>
              — {step.reason}
            </IgTypography>
          ) : null}
        </Stack>
      );
    })}
  </Stack>
);

/**
 * Glyph names a tool may use, and what they draw.
 *
 * The wire carries a name, not a component, so this is the map. Kept small and
 * additive on purpose: a tool naming something absent here still renders,
 * falling back to the status glyph rather than an empty row.
 */
const NAMED_ICONS: Record<string, LucideIcon> = {
  brand: Palette,
  catalog: Package,
  document: FileText,
  offer: Tag,
  pricing: Target,
  shipping: Truck,
};

/**
 * A running step has no icon of its own — the shimmering label is the signal,
 * and a spinner beside it would be the same thing said twice.
 */
const StepIcon = ({
  status,
  icon,
}: {
  status: ToolStep["status"];
  icon?: string;
}) => {
  // A named glyph says what the step was *about*; the status glyph says it did
  // not work. When both apply the status wins — a merchant needs to see that a
  // step failed more than they need to see which topic it belonged to.
  const Named = icon ? NAMED_ICONS[icon] : undefined;
  if (Named && status === "ok") {
    return <Named size={14} className="text-muted-foreground" />;
  }
  if (status === "ok") {
    return <Check size={14} className="text-muted-foreground" />;
  }
  if (status === "failed") {
    return <X size={14} className="text-destructive" />;
  }
  if (status === "unavailable") {
    return <Minus size={14} className="text-muted-foreground" />;
  }
  // Queued. A hollow ring rather than the running state's blank, so a row that
  // has not started is distinguishable from one that has — otherwise a
  // pre-drawn plan and a stalled step look identical.
  if (status === "pending") {
    return (
      <Circle
        size={14}
        className="text-muted-foreground"
        strokeWidth={1.5}
        aria-hidden
      />
    );
  }
  return <span className="inline-block size-[14px]" />;
};
